/**
 * Lazy-loaded export utilities to avoid bundling heavy libraries 
 * (like jspdf and xlsx-js-style) in the main chunk.
 */

export interface ExportColumn<T> {
  header: string;
  accessor: (row: T) => string | number | boolean | null | undefined;
}

export async function exportToExcel<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string = "export"
) {
  // Dynamically import xlsx-js-style
  const XLSX = await import("xlsx-js-style");

  const headers = columns.map((col) => col.header);
  const rows = data.map((row) => columns.map((col) => col.accessor(row)));

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  // Add some basic styling to headers
  for (let i = 0; i < headers.length; i++) {
    const cellRef = XLSX.utils.encode_cell({ c: i, r: 0 });
    if (!worksheet[cellRef]) continue;
    worksheet[cellRef].s = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4F46E5" } }, // Indigo-600
    };
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
  
  XLSX.writeFile(workbook, `${filename}.xlsx`);
}

// Cache the base64 font so we don't fetch it repeatedly
let cachedFontBase64: string | null = null;

async function getPersianFontBase64(): Promise<string> {
  if (cachedFontBase64) return cachedFontBase64;
  const url = "/fonts/Vazirmatn.ttf";
  const response = await fetch(url);
  const blob = await response.blob();
  const base64 = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(",")[1] || "");
    };
    reader.readAsDataURL(blob);
  });
  cachedFontBase64 = base64;
  return base64;
}

export async function exportToPDF<T>(
  data: T[],
  columns: ExportColumn<T>[],
  filename: string = "export"
) {
  try {
    // Dynamically import jspdf and jspdf-autotable
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;

    const doc = new jsPDF();
    let fontName = "helvetica";
    
    try {
      const fontBase64 = await getPersianFontBase64();
      doc.addFileToVFS("Vazirmatn.ttf", fontBase64);
      doc.addFont("Vazirmatn.ttf", "Vazirmatn", "normal", "Identity-H");
      doc.setFont("Vazirmatn");
      fontName = "Vazirmatn";
    } catch (e) {
      console.warn("Could not load Persian font for PDF", e);
    }
    
    const headers = columns.map((col) => col.header);
    const rows = data.map((row) => columns.map((col) => {
      const val = col.accessor(row);
      return val == null ? "" : String(val);
    }));

    autoTable(doc, {
      head: [headers],
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [79, 70, 229], font: fontName, fontStyle: 'normal', halign: 'right' },
      styles: { font: fontName, fontStyle: 'normal', fontSize: 10, halign: 'right' },
      bodyStyles: { font: fontName, fontStyle: 'normal', halign: 'right' },
    });

    doc.save(`${filename}.pdf`);
  } catch (error) {
    console.error("PDF Export Error:", error);
    alert("خطا در ایجاد فایل PDF. لطفا اتصال اینترنت خود را بررسی کنید.");
  }
}
